import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import NotificationBell from '../components/NotificationBell';
import HtmlContent, { stripHtml } from '../components/HtmlContent';
import { authService, cursosService } from '../services/api';
import { formatPersonName } from '../utils/personName';
import { formatDuracionClase } from '../utils/duration';
import Perfil from './Perfil';
import Usuarios from './Usuarios';
import Cursos from './Cursos';
import CursoForm from './CursoForm';
import ClaseForm from './ClaseForm';
import TareaForm from './TareaForm';
import Tareas from './Tareas';
import Clases from './Clases';
import Asistencia from './Asistencia';
import MiAsistencia from './MiAsistencia';
import MisCursos from './MisCursos';
import EntregaForm from './EntregaForm';
import CorreccionForm from './CorreccionForm';
import VerCorreccion from './VerCorreccion';
import Calificaciones from './Calificaciones';
import CursosDisponibles from './CursosDisponibles';
import Estudiantes from './Estudiantes';
import Grupos from './Grupos';
import MisGrupos from './MisGrupos';
import Estadisticas from './Estadisticas';
import InformeEditor from './InformeEditor';
import Reuniones from './Reuniones';
import NotasVersion from './NotasVersion';
import Certificados from './Certificados';
import Inscripcion from './Inscripcion';
import Configuracion from './Configuracion';
import PlantillasMail from './PlantillasMail';
import ListaNegra from './ListaNegra';
import BloquearRegistro from './BloquearRegistro';
import MiConfiguracion from './MiConfiguracion';
import Chat from './Chat';
import EnConstruccion from './EnConstruccion';
import NotFound from './NotFound';
import Calendario from '../components/Calendario';
import { CURSOS_DISPONIBLES_PAGE_SIZE } from '../constants/uploadLimits';
import {
  useHayCursosDisponibles,
  filterMenuCursosDisponibles,
  notifyCursosDisponiblesChanged,
} from '../hooks/useHayCursosDisponibles';
import { useDashboardTheme } from '../hooks/useDashboardTheme';
import ConfirmPopup from '../components/ConfirmPopup';
import './Dashboard.css';
import '../dashboard-dark.css';

const Reportes = Estadisticas; /* alias: antes /reportes, ahora /estadisticas */

/** Roles que pueden acceder a cada ruta del dashboard. null = cualquier rol autenticado. */
const ROUTE_ACCESS = {
  '/': null,
  '/perfil': null,
  '/chat': null,
  '/usuarios': ['Coordinador'],
  '/cursos': ['Coordinador', 'Profesor', 'Tutor', 'Pasante', 'Ayudante'],
  '/estadisticas': ['Coordinador'],
  '/estadisticas/informe': ['Coordinador'],
  '/certificados': ['Coordinador'],
  '/configuracion': ['Coordinador'],
  '/configuracion/plantillas-mail': ['Coordinador'],
  '/configuracion/lista-negra': ['Coordinador'],
  '/configuracion/bloquear-registro': ['Coordinador'],
  '/clases': ['Profesor', 'Tutor', 'Pasante', 'Ayudante'],
  '/tareas': ['Profesor', 'Tutor', 'Pasante', 'Ayudante'],
  '/corregir': ['Profesor', 'Tutor', 'Pasante', 'Ayudante'],
  '/ver-correccion': ['Profesor', 'Tutor', 'Pasante', 'Ayudante', 'Estudiantes', 'Estudiante'],
  '/asistencia': ['Profesor', 'Tutor', 'Pasante', 'Ayudante'],
  '/reuniones': ['Profesor', 'Tutor'],
  '/inscripcion': ['Profesor'],
  '/estudiantes': ['Profesor', 'Tutor', 'Pasante', 'Ayudante'],
  '/grupos': ['Profesor', 'Tutor', 'Pasante', 'Ayudante'],
  '/notas-version': ['Coordinador', 'Profesor', 'Tutor', 'Pasante', 'Ayudante'],
  '/cursos-disponibles': ['Estudiantes', 'Estudiante'],
  '/mis-cursos': ['Estudiantes', 'Estudiante'],
  '/mis-grupos': ['Estudiantes', 'Estudiante'],
  '/mi-configuracion': ['Estudiantes', 'Estudiante'],
  '/mi-asistencia': ['Estudiantes', 'Estudiante'],
  '/entregar': ['Estudiantes', 'Estudiante'],
  '/calificaciones': ['Estudiantes', 'Estudiante'],
};

const canAccessPath = (userGroups, path) => {
  const allowed = ROUTE_ACCESS[path];
  if (allowed === null) return true;
  if (!userGroups || !Array.isArray(userGroups) || userGroups.length === 0) return false;
  const groups = userGroups.map(g => (typeof g === 'object' && g?.name ? g.name : g));
  return groups.some(g => allowed.includes(g));
};

const ProtectedDashboardRoute = ({ path, children }) => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const userGroups = user?.groups || [];

  useEffect(() => {
    if (!canAccessPath(userGroups, path)) {
      navigate('/dashboard', { replace: true });
    }
  }, [path, userGroups, navigate]);

  if (!canAccessPath(userGroups, path)) {
    return (
      <div className="dashboard-loading">
        <p>Sin acceso a esta sección. Redirigiendo...</p>
      </div>
    );
  }
  return children;
};

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const MAX_DESC_PREVIEW = 240; // aproximación a ~5 líneas para decidir si mostrar "Ver más"

const formatHora = (horaStr) => {
  if (!horaStr) return '-';
  if (typeof horaStr === 'string' && horaStr.length >= 5) {
    return horaStr.substring(0, 5);
  }
  return horaStr;
};

/** Fecha de calendario sin desfase UTC (YYYY-MM-DD → día local). */
const formatDateOnly = (dateString) => {
  if (!dateString) return '-';
  const s = String(dateString).trim().slice(0, 10);
  const parts = s.split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) {
    return new Date(dateString).toLocaleDateString('es-AR');
  }
  const [y, m, d] = parts;
  return new Date(y, m - 1, d).toLocaleDateString('es-AR');
};

/** Mismas opciones que la Sidebar por rol (sin incluir ítem Dashboard). */
const getDashboardMenuItems = (selectedRole) => {
  const isRoleEstudiante = selectedRole === 'Estudiante' || selectedRole === 'Estudiantes';
  const baseMenu = [
    { path: '/dashboard/perfil', icon: '👤', label: 'Mi Perfil' },
    { path: '/dashboard/chat', icon: '💬', label: 'Chat' },
  ];

  // En el Dashboard, el rol Estudiante no debe ver las "Notas de la versión".
  if (selectedRole && !isRoleEstudiante) {
    baseMenu.unshift({ path: '/dashboard/notas-version', icon: '📋', label: 'Notas de la versión' });
  }
  if (!selectedRole) {
    return [...baseMenu];
  }
  switch (selectedRole) {
    case 'Coordinador':
      return [
        ...baseMenu,
        { path: '/dashboard/usuarios', icon: '👥', label: 'Gestión de Usuarios' },
        { path: '/dashboard/cursos', icon: '📚', label: 'Cursos' },
        { path: '/dashboard/certificados', icon: '📜', label: 'Certificados' },
        { path: '/dashboard/configuracion', icon: '⚙️', label: 'Configuración' },
        { path: '/dashboard/estadisticas', icon: '📈', label: 'Estadísticas' },
      ];
    case 'Profesor':
      return [
        ...baseMenu,
        { path: '/dashboard/cursos', icon: '📚', label: 'Cursos' },
        { path: '/dashboard/inscripcion', icon: '✍️', label: 'Inscripción' },
        { path: '/dashboard/clases', icon: '🎓', label: 'Clases' },
        { path: '/dashboard/tareas', icon: '📝', label: 'Tareas' },
        { path: '/dashboard/grupos', icon: '👥', label: 'Grupos' },
        { path: '/dashboard/asistencia', icon: '📋', label: 'Asistencia' },
        { path: '/dashboard/reuniones', icon: '💻', label: 'Reuniones' },
        { path: '/dashboard/estudiantes', icon: '👨‍🎓', label: 'Estudiantes' },
      ];
    case 'Tutor':
      return [
        ...baseMenu,
        { path: '/dashboard/cursos', icon: '📚', label: 'Cursos' },
        { path: '/dashboard/clases', icon: '🎓', label: 'Clases' },
        { path: '/dashboard/tareas', icon: '📝', label: 'Tareas' },
        { path: '/dashboard/grupos', icon: '👥', label: 'Grupos' },
        { path: '/dashboard/asistencia', icon: '📋', label: 'Asistencia' },
        { path: '/dashboard/reuniones', icon: '💻', label: 'Reuniones' },
      ];
    case 'Pasante':
      return [
        ...baseMenu,
        { path: '/dashboard/tareas', icon: '📝', label: 'Tareas' },
        { path: '/dashboard/grupos', icon: '👥', label: 'Grupos' },
        { path: '/dashboard/asistencia', icon: '📋', label: 'Asistencia' },
      ];
    case 'Ayudante':
      return [
        ...baseMenu,
        { path: '/dashboard/tareas', icon: '📝', label: 'Tareas' },
        { path: '/dashboard/grupos', icon: '👥', label: 'Grupos' },
        { path: '/dashboard/asistencia', icon: '📋', label: 'Asistencia' },
      ];
    default: // Estudiante / Estudiantes
      return [
        ...baseMenu,
        { path: '/dashboard/cursos-disponibles', icon: '📖', label: 'Cursos disponibles' },
        { path: '/dashboard/mis-cursos', icon: '📚', label: 'Mis Cursos' },
        { path: '/dashboard/mi-asistencia', icon: '📋', label: 'Mi Asistencia' },
        { path: '/dashboard/calificaciones', icon: '📊', label: 'Correcciones' },
        { path: '/dashboard/mis-grupos', icon: '👥', label: 'Mis grupos' },
        { path: '/dashboard/mi-configuracion', icon: '⚙️', label: 'Configuración' },
      ];
  }
};

const DashboardHome = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [cursosDisponibles, setCursosDisponibles] = useState([]);
  const [loadingCursos, setLoadingCursos] = useState(false);
  const [expandidoId, setExpandidoId] = useState(null);
  const [inscribiendoId, setInscribiendoId] = useState(null);
  const [mensajeInscripcion, setMensajeInscripcion] = useState({ tipo: '', texto: '' });
  const [cursoDescripcionModal, setCursoDescripcionModal] = useState(null);
  const carouselRef = useRef(null);
  const [carouselCanPrev, setCarouselCanPrev] = useState(false);
  const [carouselCanNext, setCarouselCanNext] = useState(true);
  const getCarouselStep = () => 340; /* una tarjeta (320px) + gap para scroll con flechas */
  const scrollTolerance = 60; /* considerar "al final" si queda menos de esto por recorrer */

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    if (cursosDisponibles.length <= 2) {
      setCarouselCanPrev(false);
      setCarouselCanNext(false);
      return;
    }
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const maxScroll = scrollWidth - clientWidth;
      setCarouselCanPrev(scrollLeft > scrollTolerance);
      setCarouselCanNext(maxScroll > scrollTolerance && scrollLeft < maxScroll - scrollTolerance);
    };
    update();
    const t = setTimeout(update, 100); /* recheck tras pintar */
    el.addEventListener('scroll', update);
    return () => {
      clearTimeout(t);
      el.removeEventListener('scroll', update);
    };
  }, [cursosDisponibles]);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    const rol = localStorage.getItem('selectedRole');
    setSelectedRole(rol || (currentUser?.groups?.[0]) || 'Estudiante');

    authService.getProfile()
      .then(profile => {
        setUser(profile);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleRoleChange = () => {
      const rol = localStorage.getItem('selectedRole');
      if (rol) setSelectedRole(rol);
    };
    window.addEventListener('roleChanged', handleRoleChange);
    return () => window.removeEventListener('roleChanged', handleRoleChange);
  }, []);

  const esEstudiante = selectedRole === 'Estudiante' || selectedRole === 'Estudiantes';
  const showCursosDisponiblesNav = !loadingCursos && cursosDisponibles.length > 0;

  useEffect(() => {
    if (esEstudiante) {
      setLoadingCursos(true);
      cursosService.getCursosDisponibles({ page: 1, page_size: CURSOS_DISPONIBLES_PAGE_SIZE })
        .then(({ results }) => {
          setCursosDisponibles(results);
          notifyCursosDisponiblesChanged();
        })
        .catch(() => {
          setCursosDisponibles([]);
          notifyCursosDisponiblesChanged();
        })
        .finally(() => setLoadingCursos(false));
    }
  }, [esEstudiante]);

  const toggleVerMas = (id) => {
    setExpandidoId(prev => prev === id ? null : id);
  };

  const handleInscribirme = async (cursoId) => {
    setMensajeInscripcion({ tipo: '', texto: '' });
    setInscribiendoId(cursoId);
    try {
      await cursosService.preInscribirseEnCurso(cursoId);
      setMensajeInscripcion({
        tipo: 'ok',
        texto: 'Te has pre-inscripto exitosamente. La pre-inscripción no garantiza lugar en el curso.',
      });
      cursosService.getCursosDisponibles({ page: 1, page_size: CURSOS_DISPONIBLES_PAGE_SIZE })
        .then(({ results }) => {
          setCursosDisponibles(results);
          notifyCursosDisponiblesChanged();
        })
        .catch(() => {
          setCursosDisponibles([]);
          notifyCursosDisponiblesChanged();
        });
      window.dispatchEvent(new CustomEvent('calendarRefresh'));
    } catch (err) {
      const msg = err.response?.data?.error || 'No se pudo completar la pre-inscripción.';
      setMensajeInscripcion({ tipo: 'error', texto: msg });
    } finally {
      setInscribiendoId(null);
    }
  };

  return (
    <div className="dashboard-home-wrap">
      <div className="dashboard-home-main">
        <div className="dashboard-header">
          <div className="dashboard-header-title-row">
            <h1>Panel Principal</h1>
            <NotificationBell rol={selectedRole} />
          </div>
          <p>Bienvenid@ de vuelta, {formatPersonName(user?.first_name) || 'Usuario'}</p>
        </div>

        {esEstudiante ? (
        <>
          {showCursosDisponiblesNav && (
          <section className="dashboard-section">
            <h2 className="dashboard-section-title">Cursos con inscripción abierta</h2>
              <>
                {mensajeInscripcion.texto && mensajeInscripcion.tipo === 'ok' && (
                  <div className="dashboard-inscripcion-msg dashboard-inscripcion-ok">
                    {mensajeInscripcion.texto}
                  </div>
                )}
                <ConfirmPopup
                  open={Boolean(mensajeInscripcion.texto) && mensajeInscripcion.tipo === 'error'}
                  title="Error"
                  message={mensajeInscripcion.texto}
                  confirmLabel="Aceptar"
                  cancelLabel={null}
                  onConfirm={() => setMensajeInscripcion({ tipo: '', texto: '' })}
                  onCancel={() => setMensajeInscripcion({ tipo: '', texto: '' })}
                  variant="danger"
                />
                <div className="dashboard-cursos-carousel">
                  <button
                    type="button"
                    className="dashboard-carousel-btn dashboard-carousel-prev"
                    onClick={() => {
                      const el = carouselRef.current;
                      if (!el || !carouselCanPrev) return;
                      const step = getCarouselStep();
                      el.scrollBy({ left: -Math.min(step, el.scrollLeft), behavior: 'smooth' });
                    }}
                    aria-label="Anterior"
                    disabled={!carouselCanPrev}
                  >
                    ‹
                  </button>
                  <div className="dashboard-cursos-track" ref={carouselRef}>
                    {cursosDisponibles.map((curso) => {
                      const nivelNombre = curso.nivel_nombre || (curso.nivel && typeof curso.nivel === 'object' && curso.nivel.nombre ? curso.nivel.nombre : null);
                      const descPlain = stripHtml(curso.descripcion);
                      const descEsLarga = descPlain.length > MAX_DESC_PREVIEW;
                      return (
                    <div key={curso.id} className="dashboard-curso-card">
                      {nivelNombre && (
                        <span className="dashboard-curso-badge-nivel" aria-label={`Nivel: ${nivelNombre}`}>
                          {nivelNombre}
                        </span>
                      )}
                      <div className="dashboard-curso-card-header">
                        <h3>{curso.nombre}</h3>
                      </div>
                      {curso.descripcion && (
                        <HtmlContent content={curso.descripcion} tag="div" className="dashboard-curso-desc" />
                      )}
                      <div className="dashboard-curso-actions">
                        {descEsLarga && (
                          <button
                            type="button"
                            className="dashboard-curso-vermas"
                            onClick={() => setCursoDescripcionModal(curso)}
                            title="Ver descripción completa"
                          >
                            Ver más
                          </button>
                        )}
                        <button
                          type="button"
                          className="dashboard-curso-vermas"
                          onClick={() => toggleVerMas(curso.id)}
                          aria-expanded={expandidoId === curso.id}
                        >
                          {expandidoId === curso.id ? 'Ocultar clases' : 'Ver clases'}
                        </button>
                        {curso.estoy_inscripto ? (
                          <span className="dashboard-curso-ya-inscripto">Ya inscripto</span>
                        ) : curso.estoy_preinscripto ? (
                          <span className="dashboard-curso-preinscripto">Pre-inscripto (pendiente)</span>
                        ) : (
                          <button
                            type="button"
                            className="dashboard-curso-inscribirme"
                            onClick={() => handleInscribirme(curso.id)}
                            disabled={inscribiendoId === curso.id}
                          >
                            {inscribiendoId === curso.id ? 'Inscribiendo...' : 'Inscribirme'}
                          </button>
                        )}
                      </div>
                      {expandidoId === curso.id && (
                        <div className="dashboard-curso-detalle">
                          <div className="dashboard-curso-dias">
                            <strong>Día(s) de clase:</strong>{' '}
                            {(curso.dias_semana && curso.dias_semana.length > 0)
                              ? curso.dias_semana.map(d => DIAS_SEMANA[d]).join(', ')
                              : 'No definido'}
                          </div>
                          <div className="dashboard-curso-horario">
                            <strong>Horario:</strong> {formatHora(curso.hora_inicio_clase)}
                            {(() => {
                              const d = formatDuracionClase(curso.duracion_clase);
                              return d ? ` (duración ${d})` : '';
                            })()}
                          </div>
                          {curso.fecha_inicio && curso.fecha_fin && (
                            <div className="dashboard-curso-fechas">
                              <strong>Período:</strong> {formatDateOnly(curso.fecha_inicio)} – {formatDateOnly(curso.fecha_fin)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                  })}
                  </div>
                  <button
                    type="button"
                    className="dashboard-carousel-btn dashboard-carousel-next"
                    onClick={() => {
                      const el = carouselRef.current;
                      if (!el || !carouselCanNext) return;
                      const maxScroll = el.scrollWidth - el.clientWidth;
                      const step = getCarouselStep();
                      el.scrollBy({ left: Math.min(step, maxScroll - el.scrollLeft), behavior: 'smooth' });
                    }}
                    aria-label="Siguiente"
                    disabled={!carouselCanNext}
                  >
                    ›
                  </button>
                </div>
              </>
          </section>
          )}
          <div className="dashboard-grid">
            {filterMenuCursosDisponibles(
              getDashboardMenuItems(selectedRole),
              showCursosDisponiblesNav
            ).map((item) => (
              <button
                key={item.path}
                type="button"
                className="dashboard-card"
                onClick={() => navigate(item.path)}
              >
                <div className="dashboard-card-icon">{item.icon}</div>
                <h3>{item.label}</h3>
              </button>
            ))}
          </div>
        </>
      ) : (
        <div className="dashboard-grid">
          {getDashboardMenuItems(selectedRole).map((item) => (
            <button
              key={item.path}
              type="button"
              className="dashboard-card"
              onClick={() => navigate(item.path)}
            >
              <div className="dashboard-card-icon">{item.icon}</div>
              <h3>{item.label}</h3>
            </button>
          ))}
        </div>
      )}
      </div>
      {esEstudiante && (
        <aside className="dashboard-home-calendario">
          <Calendario />
        </aside>
      )}
      {cursoDescripcionModal && (
        <div
          className="dashboard-curso-desc-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dashboard-curso-desc-modal-title"
        >
          <div
            className="dashboard-curso-desc-modal-box"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dashboard-curso-desc-modal-header">
              <h3 id="dashboard-curso-desc-modal-title">
                Descripción de: {cursoDescripcionModal.nombre}
              </h3>
              <button
                type="button"
                className="dashboard-curso-desc-modal-close"
                onClick={() => setCursoDescripcionModal(null)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="dashboard-curso-desc-modal-body">
              {cursoDescripcionModal.descripcion && (
                <HtmlContent
                  content={cursoDescripcionModal.descripcion}
                  tag="div"
                  className="dashboard-curso-desc"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    try {
      return localStorage.getItem('campus_sidebar_pinned') === '1';
    } catch {
      return false;
    }
  });
  const { theme, toggleTheme } = useDashboardTheme();
  const currentUser = authService.getCurrentUser();
  const rawRole = typeof window !== 'undefined' ? (localStorage.getItem('selectedRole') || currentUser?.groups?.[0]) : null;
  const selectedRole = typeof rawRole === 'object' && rawRole?.name ? rawRole.name : rawRole;
  const isEstudiante = selectedRole === 'Estudiante' || selectedRole === 'Estudiantes';
  const { hayCursos, loading: loadingCursosDisponibles } = useHayCursosDisponibles(isEstudiante);
  const showCursosDisponiblesNav = !isEstudiante || (!loadingCursosDisponibles && hayCursos);
  const menuPaths = filterMenuCursosDisponibles(
    getDashboardMenuItems(selectedRole),
    showCursosDisponiblesNav
  ).map((i) => i.path);
  const pathInSidebar = menuPaths.includes(location.pathname);

  useEffect(() => {
    // Verificar si el usuario está autenticado
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    // Obtener perfil actualizado desde el servidor y verificar si está completo
    authService.getProfile()
      .then(profile => {
        // Si el perfil no está completo, redirigir a completar perfil
        if (profile.profile_complete === false) {
          navigate('/complete-profile');
          return;
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [navigate]);

  if (loading) {
    return (
      <div className="dashboard-container" data-theme={theme}>
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`dashboard-container${sidebarPinned ? ' dashboard-sidebar-pinned' : ''}`}
      data-theme={theme}
    >
      <Sidebar theme={theme} onToggleTheme={toggleTheme} onPinnedChange={setSidebarPinned} />
      <div className="dashboard-content">
        <Routes>
          <Route path="/" element={<ProtectedDashboardRoute path="/"><DashboardHome /></ProtectedDashboardRoute>} />
          <Route path="/perfil" element={<ProtectedDashboardRoute path="/perfil"><Perfil /></ProtectedDashboardRoute>} />
          <Route path="/usuarios" element={<ProtectedDashboardRoute path="/usuarios"><Usuarios /></ProtectedDashboardRoute>} />
          <Route path="/cursos" element={<ProtectedDashboardRoute path="/cursos"><Cursos /></ProtectedDashboardRoute>} />
          <Route path="/cursos/nuevo" element={<ProtectedDashboardRoute path="/cursos"><CursoForm /></ProtectedDashboardRoute>} />
          <Route path="/cursos/:id/editar" element={<ProtectedDashboardRoute path="/cursos"><CursoForm /></ProtectedDashboardRoute>} />
          <Route
            path="/cursos-disponibles"
            element={
              isEstudiante && !loadingCursosDisponibles && !hayCursos ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <ProtectedDashboardRoute path="/cursos-disponibles"><CursosDisponibles /></ProtectedDashboardRoute>
              )
            }
          />
          <Route path="/clases" element={<ProtectedDashboardRoute path="/clases"><Clases /></ProtectedDashboardRoute>} />
          <Route path="/clases/nuevo" element={<ProtectedDashboardRoute path="/clases"><ClaseForm /></ProtectedDashboardRoute>} />
          <Route path="/clases/:id/editar" element={<ProtectedDashboardRoute path="/clases"><ClaseForm /></ProtectedDashboardRoute>} />
          <Route path="/tareas" element={<ProtectedDashboardRoute path="/tareas"><Tareas /></ProtectedDashboardRoute>} />
          <Route path="/tareas/nuevo" element={<ProtectedDashboardRoute path="/tareas"><TareaForm /></ProtectedDashboardRoute>} />
          <Route path="/tareas/:id/editar" element={<ProtectedDashboardRoute path="/tareas"><TareaForm /></ProtectedDashboardRoute>} />
          <Route path="/corregir/:entregaId" element={<ProtectedDashboardRoute path="/corregir"><CorreccionForm /></ProtectedDashboardRoute>} />
          <Route path="/ver-correccion/:entregaId" element={<ProtectedDashboardRoute path="/ver-correccion"><VerCorreccion /></ProtectedDashboardRoute>} />
          <Route path="/asistencia" element={<ProtectedDashboardRoute path="/asistencia"><Asistencia /></ProtectedDashboardRoute>} />
          <Route path="/reuniones" element={<ProtectedDashboardRoute path="/reuniones"><Reuniones /></ProtectedDashboardRoute>} />
          <Route path="/mi-asistencia" element={<ProtectedDashboardRoute path="/mi-asistencia"><MiAsistencia /></ProtectedDashboardRoute>} />
          <Route path="/mis-cursos" element={<ProtectedDashboardRoute path="/mis-cursos"><MisCursos /></ProtectedDashboardRoute>} />
          <Route path="/entregar/:tareaId" element={<ProtectedDashboardRoute path="/entregar"><EntregaForm /></ProtectedDashboardRoute>} />
          <Route path="/calificaciones" element={<ProtectedDashboardRoute path="/calificaciones"><Calificaciones /></ProtectedDashboardRoute>} />
          <Route path="/estudiantes" element={<ProtectedDashboardRoute path="/estudiantes"><Estudiantes /></ProtectedDashboardRoute>} />
          <Route path="/grupos" element={<ProtectedDashboardRoute path="/grupos"><Grupos /></ProtectedDashboardRoute>} />
          <Route path="/mis-grupos" element={<ProtectedDashboardRoute path="/mis-grupos"><MisGrupos /></ProtectedDashboardRoute>} />
          <Route path="/inscripcion" element={<ProtectedDashboardRoute path="/inscripcion"><Inscripcion /></ProtectedDashboardRoute>} />
          <Route path="/estadisticas" element={<ProtectedDashboardRoute path="/estadisticas"><Estadisticas /></ProtectedDashboardRoute>} />
          <Route path="/estadisticas/informe" element={<ProtectedDashboardRoute path="/estadisticas"><InformeEditor /></ProtectedDashboardRoute>} />
          <Route path="/certificados" element={<ProtectedDashboardRoute path="/certificados"><Certificados /></ProtectedDashboardRoute>} />
          <Route path="/configuracion" element={<ProtectedDashboardRoute path="/configuracion"><Configuracion /></ProtectedDashboardRoute>} />
          <Route path="/configuracion/plantillas-mail" element={<ProtectedDashboardRoute path="/configuracion/plantillas-mail"><PlantillasMail /></ProtectedDashboardRoute>} />
          <Route path="/configuracion/lista-negra" element={<ProtectedDashboardRoute path="/configuracion/lista-negra"><ListaNegra /></ProtectedDashboardRoute>} />
          <Route path="/configuracion/bloquear-registro" element={<ProtectedDashboardRoute path="/configuracion/bloquear-registro"><BloquearRegistro /></ProtectedDashboardRoute>} />
          <Route path="/mi-configuracion" element={<ProtectedDashboardRoute path="/mi-configuracion"><MiConfiguracion /></ProtectedDashboardRoute>} />
          <Route path="/notas-version" element={<ProtectedDashboardRoute path="/notas-version"><NotasVersion /></ProtectedDashboardRoute>} />
          <Route path="/chat" element={<ProtectedDashboardRoute path="/chat"><Chat /></ProtectedDashboardRoute>} />
          <Route path="*" element={pathInSidebar ? <EnConstruccion /> : <NotFound redirectTo="/dashboard" />} />
        </Routes>
      </div>
    </div>
  );
};

export default Dashboard;



